
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Upload, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useBuyerProfile } from '@/hooks/useBuyerProfile';
import { LocationAutocomplete } from '@/components/dashboard/LocationAutocomplete';
import { compressImage, uploadToStorage } from '@/lib/imageCompression';

export const BuyerProfileSection: React.FC = () => {
  const { user } = useAuth();
  const {
    formData,
    loading,
    hasChanges,
    handleInputChange,
    handleLocationChange,
    handleImageUpdate,
    handleSaveChanges
  } = useBuyerProfile();

  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = async (file: File) => {
    if (!user) return;

    setIsUploading(true);
    try {
      const compressed = await compressImage(file);
      const publicUrl = await uploadToStorage(compressed, user.id, 'profile-images');
      handleImageUpdate(publicUrl);
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageClick = () => {
    if (isUploading) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        handleImageUpload(file);
      }
    };
    input.click();
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center space-x-6">
          <div className="flex flex-col items-center">
            <div className="relative">
              <div className="w-36 h-36 rounded-full overflow-hidden border-4 border-white shadow-lg cursor-pointer hover:opacity-80 transition-opacity">
                {formData.profileImage ? (
                  <img 
                    src={formData.profileImage} 
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-green-700 flex items-center justify-center text-white font-semibold text-lg">
                    {getInitials(formData.name)}
                  </div>
                )}
                {isUploading && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-full">
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  </div>
                )}
              </div>
              <Button
                variant="outline"
                size="icon"
                className="absolute bottom-0 right-0 rounded-full w-8 h-8 bg-white hover:bg-gray-50 border-2 border-white shadow-lg"
                onClick={handleImageClick}
                disabled={isUploading}
              >
                {isUploading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Upload className="w-3 h-3" />
                )}
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input 
              id="name" 
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />
          </div>
          <LocationAutocomplete
            value={formData.location}
            onChange={handleLocationChange}
            placeholder="Enter your location..."
          />
        </div>

        <div>
          <Label htmlFor="bio">Bio</Label>
          <Textarea 
            id="bio" 
            value={formData.bio}
            onChange={(e) => handleInputChange('bio', e.target.value)}
            rows={4}
            placeholder="Tell us about yourself..."
          />
        </div>

        {hasChanges && (
          <Button 
            className="bg-green-700 hover:bg-green-800"
            onClick={handleSaveChanges}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
